import * as Sentry from '@sentry/browser';

import { createTracesSampler, isLocalEnvironment, resolveSentryEnvironment } from './sentry_sampling.js';

const SENTRY_TOOLBAR_SCRIPT_ID = 'sentry-toolbar-cdn';

function getSentryClientConfig() {
    const configElement = document.getElementById('classweb-sentry-config');

    if (!configElement || !configElement.textContent) {
        return {};
    }

    try {
        return JSON.parse(configElement.textContent);
    } catch {
        return {};
    }
}

function getSentryToolbarConfig() {
    const clientConfig = getSentryClientConfig();

    return clientConfig.toolbar || null;
}

function getSentryToolbarScriptUrl(version) {
    return (
        'https://browser.sentry-cdn.com/sentry-toolbar/' + encodeURIComponent(version || 'latest') + '/toolbar.min.js'
    );
}

function shouldInitializeSentryToolbar(toolbarConfig) {
    if (!toolbarConfig || !toolbarConfig.enabled) {
        return false;
    }

    if (!toolbarConfig.organizationSlug || !toolbarConfig.projectIdOrSlug) {
        return false;
    }

    const environments = Array.isArray(toolbarConfig.environments) ? toolbarConfig.environments : [];

    return environments.includes(toolbarConfig.currentEnvironment) || toolbarConfig.userIsStaff === true;
}

function loadSentryToolbarScript(scriptUrl) {
    if (window.SentryToolbar) {
        return Promise.resolve(window.SentryToolbar);
    }

    const existingScript = document.getElementById(SENTRY_TOOLBAR_SCRIPT_ID);
    if (existingScript) {
        return new Promise((resolve, reject) => {
            existingScript.addEventListener('load', () => resolve(window.SentryToolbar), { once: true });
            existingScript.addEventListener('error', reject, { once: true });
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = SENTRY_TOOLBAR_SCRIPT_ID;
        script.src = scriptUrl;
        script.crossOrigin = 'anonymous';
        script.referrerPolicy = 'origin';
        script.addEventListener(
            'load',
            () => {
                if (!window.SentryToolbar) {
                    reject(new Error('Sentry Toolbar script loaded without a window.SentryToolbar global.'));
                    return;
                }

                resolve(window.SentryToolbar);
            },
            { once: true }
        );
        script.addEventListener('error', reject, { once: true });
        document.body.appendChild(script);
    });
}

async function initializeSentryToolbar() {
    const toolbarConfig = getSentryToolbarConfig();

    if (!shouldInitializeSentryToolbar(toolbarConfig)) {
        return;
    }

    try {
        const toolbar = await loadSentryToolbarScript(getSentryToolbarScriptUrl(toolbarConfig.version));
        toolbar.init({
            organizationSlug: toolbarConfig.organizationSlug,
            projectIdOrSlug: toolbarConfig.projectIdOrSlug,
            environment: [toolbarConfig.currentEnvironment],
            sentryOrigin: toolbarConfig.sentryOrigin,
            placement: 'bottom-right-corner',
            theme: 'system',
        });
    } catch (error) {
        console.warn('Unable to initialize Sentry Toolbar', error);
    }
}

// Transaction filtering to reduce noise and invalid transactions
function beforeSendTransaction(event) {
    // Filter out health checks and monitoring endpoints
    if (event.transaction) {
        const transactionName = event.transaction.toLowerCase();

        // Skip noisy transactions
        if (
            transactionName.includes('favicon') ||
            transactionName.includes('robots.txt') ||
            transactionName.includes('/_debug_toolbar/') ||
            transactionName.includes('/static/') ||
            transactionName.includes('/media/') ||
            transactionName.includes('/admin/jsi18n/') ||
            transactionName.includes('/admin/autocomplete/')
        ) {
            return null;
        }

        // Filter out very short transactions (likely invalid)
        if (event.spans && event.spans.length === 0) {
            const duration = event.timestamp - event.start_timestamp;
            if (duration < 0.001) {
                // Less than 1ms
                return null;
            }
        }
    }

    return event;
}

// Session Replay is the largest part of the SDK and is not needed to report
// errors or measure the page load, so it is fetched once the page is idle.
// Errors thrown before then are still reported, without a replay buffer.
function loadReplayWhenIdle() {
    const addReplay = async () => {
        try {
            const [{ replayIntegration }, { replayCanvasIntegration }] = await Promise.all([
                import('@sentry/replay'),
                import('@sentry/replay-canvas'),
            ]);
            Sentry.addIntegration(replayIntegration({ maskAllText: false, blockAllMedia: false }));
            Sentry.addIntegration(replayCanvasIntegration());
        } catch (error) {
            console.warn('Unable to load Sentry Session Replay', error);
        }
    };
    const whenIdle = () =>
        'requestIdleCallback' in window ? window.requestIdleCallback(addReplay) : window.setTimeout(addReplay, 1);

    if (document.readyState === 'complete') {
        whenIdle();
    } else {
        window.addEventListener('load', whenIdle, { once: true });
    }
}

// Only initialize Sentry if a DSN is provided
const sentryClientConfig = getSentryClientConfig();
const sentryEnvironment = resolveSentryEnvironment(sentryClientConfig);
const isLocalSentryEnvironment = isLocalEnvironment(sentryEnvironment);
if (sentryClientConfig.dsn) {
    Sentry.init({
        dsn: sentryClientConfig.dsn,
        debug: isLocalSentryEnvironment,
        environment: sentryEnvironment,
        release: process.env.SENTRY_RELEASE,
        initialScope: {
            tags: {
                'source.branch': process.env.SENTRY_BRANCH || 'unknown',
            },
        },

        // Enable logs to be sent to Sentry
        enableLogs: true,

        integrations: [
            Sentry.browserTracingIntegration({
                // Configure tracing integration to reduce noise
                enableInp: true,
                enableLcp: true,
                enableFcp: true,
            }),
            Sentry.browserProfilingIntegration(),
            // Session Replay is added by loadReplayWhenIdle()
            // Send console.log, console.warn, and console.error calls as logs to Sentry
            Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
        ],

        // Session Replay sample rates, read when the replay integration is added
        replaysSessionSampleRate: isLocalSentryEnvironment ? 0.3 : 0.2,
        replaysOnErrorSampleRate: 1.0,

        // Use dynamic sampling instead of fixed rate
        tracesSampler: createTracesSampler(sentryEnvironment),

        // Set trace propagation targets to prevent noise from external resources
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/avservices\.gatech\.edu\/.*/,
            /^https:\/\/.*\.gatech\.edu\/.*/,
        ],

        // Higher profiling rates for initial data collection
        profilesSampleRate: isLocalSentryEnvironment ? 1.0 : 0.3,

        // Transaction filtering
        beforeSendTransaction: beforeSendTransaction,

        // Additional configuration to reduce noise
        beforeSend(event) {
            // Filter out common browser errors that aren't actionable
            if (event.exception) {
                const error = event.exception.values[0];
                if (error && error.type) {
                    // Filter out network errors and script loading errors
                    if (
                        error.type.includes('ChunkLoadError') ||
                        error.type.includes('NetworkError') ||
                        error.type.includes('ResizeObserver')
                    ) {
                        return null;
                    }
                }
            }
            return event;
        },

        // Filter logs to reduce noise
        beforeSendLog(log) {
            // In development, allow more logs for debugging
            if (isLocalSentryEnvironment) {
                return log;
            }

            // In production, filter out debug and trace logs
            if (log.level === 'debug' || log.level === 'trace') {
                return null;
            }

            // Filter out common noisy log messages
            if (log.message && typeof log.message === 'string') {
                const message = log.message.toLowerCase();
                if (
                    message.includes('favicon') ||
                    message.includes('robots.txt') ||
                    (message.includes('webpack') && message.includes('hot update'))
                ) {
                    return null;
                }
            }

            return log;
        },
    });

    loadReplayWhenIdle();
} else {
    console.log('Sentry DSN not provided, skipping Sentry initialization.');
}

// Identify user for error monitoring (see https://security.gatech.edu/terms-of-use)
if (window.SENTRY_USER) {
    Sentry.setUser({
        id: window.SENTRY_USER.id,
        username: window.SENTRY_USER.username,
        email: window.SENTRY_USER.email,
    });
}

initializeSentryToolbar();
